import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { initializeMsal } from './auth/msalConfig';
import { AuthProvider } from './auth/AuthContext';
import App from './App';
import './index.css';

// ── Sentry error tracking ───────────────────────────────────
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return;
      Sentry.captureException(error, {
        tags: { source: 'query' },
        contexts: { query: { key: JSON.stringify(query.queryKey) } },
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      const mutationKey = mutation.options.mutationKey;
      Sentry.captureException(error, {
        tags: { source: 'mutation' },
        contexts: { mutation: { key: mutationKey ? JSON.stringify(mutationKey) : undefined } },
      });
    },
  }),
});

async function bootstrap() {
  const { instance, config } = await initializeMsal();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider msalInstance={instance} config={config}>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap().catch((err) => {
  console.error('Bootstrap failed:', err);
  document.getElementById('root')!.innerHTML =
    '<div style="padding:2rem;font-family:sans-serif;color:#ef4444"><h2>Failed to start</h2><pre>' +
    String(err) + '</pre></div>';
});
